/**
 * Document Upload Components
 * 
 * Mobile-friendly document upload for fit notes, MATB1, etc.
 * Supports camera capture and file selection.
 */

import React, { useState, useRef, useCallback } from 'react';
import api from './smartApi';
import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  CardActions,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  LinearProgress,
  Tooltip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  CloudUpload,
  CameraAlt,
  InsertDriveFile,
  Description,
  CheckCircle,
  Cancel,
  Delete,
  Visibility,
  Download,
  VerifiedUser,
  Warning,
  Image as ImageIcon,
  PictureAsPdf
} from '@mui/icons-material';


// Document type configuration
const DOCUMENT_TYPES = {
  fit_note: {
    name: 'Fit Note',
    description: 'Statement of Fitness for Work from your GP or hospital',
    icon: '📋',
    hasDateFields: true,
    hasStatusField: true
  },
  self_certification: {
    name: 'Self-Certification',
    description: 'For absences of 7 days or less',
    icon: '✍️',
    hasDateFields: false,
    hasStatusField: false
  },
  matb1: {
    name: 'MATB1 Certificate',
    description: 'Maternity certificate from your midwife or GP',
    icon: '👶',
    hasDateFields: false,
    hasStatusField: false
  },
  medical_evidence: {
    name: 'Medical Evidence',
    description: 'Hospital letter, appointment confirmation, etc.',
    icon: '🏥',
    hasDateFields: false,
    hasStatusField: false
  },
  other: {
    name: 'Other Document',
    description: 'Any other supporting documentation',
    icon: '📎',
    hasDateFields: false,
    hasStatusField: false
  }
};


// Builds the multipart body for an upload. Used for an immediate upload and for one queued until a new
// request exists.
const buildUploadForm = (q, sourceTable, sourceId) => {
  const formData = new FormData();
  formData.append('file', q.file);
  formData.append('source_table', sourceTable);
  formData.append('source_id', sourceId);
  formData.append('document_type', q.documentType);
  if (q.documentType === 'fit_note') {
    if (q.fitNoteStart) formData.append('fit_note_start', q.fitNoteStart);
    if (q.fitNoteEnd) formData.append('fit_note_end', q.fitNoteEnd);
    if (q.fitNoteStatus) formData.append('fit_note_status', q.fitNoteStatus);
  }
  if (q.notes) formData.append('notes', q.notes);
  return formData;
};

// Sends a document that was chosen before its request existed (see onFileQueued below).
export const uploadQueuedDocument = (q, sourceTable, sourceId) =>
  api.post('/time-off-docs/upload', buildUploadForm(q, sourceTable, sourceId), {
    headers: { 'Content-Type': 'multipart/form-data' }
  });


/**
 * Main Document Upload Component
 * Use this to add document upload to any absence/request form.
 * 
 * Props:
 *   sourceTable: 'absences' | 'time_off_requests' | 'flexible_working'
 *   sourceId: ID of the parent record
 *   documentType: Optional default document type
 *   onUploadComplete: Callback when upload succeeds
 *   onFileQueued: Used when there is no sourceId yet (a request that has not been submitted). The chosen
 *                 file is handed to this callback instead of being sent, and the caller sends it with
 *                 uploadQueuedDocument() once the record exists. Called with null if the file is removed.
 *   compact: Boolean for compact mode (single button)
 */
export const DocumentUpload = ({ 
  sourceTable, 
  sourceId, 
  documentType: defaultType = 'fit_note',
  onUploadComplete,
  onFileQueued,
  compact = false
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [queuedName, setQueuedName] = useState('');
  const queueMode = !sourceId && !!onFileQueued;
  
  // Form fields
  const [documentType, setDocumentType] = useState(defaultType);
  const [fitNoteStart, setFitNoteStart] = useState('');
  const [fitNoteEnd, setFitNoteEnd] = useState('');
  const [fitNoteStatus, setFitNoteStatus] = useState('not_fit');
  const [notes, setNotes] = useState('');
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'application/pdf'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|heic|heif|pdf)$/i)) {
      setError('Please upload a photo (JPG, PNG) or PDF file');
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File is too large. Maximum size is 10MB');
      return;
    }
    
    setError(null);
    setSelectedFile(file);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
    
    // Open dialog for additional details
    setDialogOpen(true);
  }, []);

  // Trigger camera capture
  const handleCameraCapture = () => {
    cameraInputRef.current?.click();
  };

  // Trigger file selection
  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  // Upload the document
  const handleUpload = async () => {
    if (!selectedFile) return;

    // No record to attach to yet: hand the file back to the form, which sends it after submitting.
    if (!sourceId) {
      if (!onFileQueued) return;
      onFileQueued({ file: selectedFile, documentType, notes, fitNoteStart, fitNoteEnd, fitNoteStatus });
      setQueuedName(selectedFile.name);
      setDialogOpen(false);
      setSelectedFile(null);
      setPreview(null);
      setFitNoteStart('');
      setFitNoteEnd('');
      setNotes('');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);
    
    try {
      const formData = buildUploadForm(
        { file: selectedFile, documentType, notes, fitNoteStart, fitNoteEnd, fitNoteStatus },
        sourceTable, sourceId
      );
      
      const response = await api.post('/time-off-docs/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      // Success
      setDialogOpen(false);
      setSelectedFile(null);
      setPreview(null);
      setFitNoteStart('');
      setFitNoteEnd('');
      setNotes('');
      
      if (onUploadComplete) {
        onUploadComplete(response.data);
      }
      
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeQueued = () => {
    setQueuedName('');
    if (onFileQueued) onFileQueued(null);
  };

  // Reset form
  const handleCancel = () => {
    setDialogOpen(false);
    setSelectedFile(null);
    setPreview(null);
    setError(null);
    setFitNoteStart('');
    setFitNoteEnd('');
    setNotes('');
  };

  const typeConfig = DOCUMENT_TYPES[documentType] || DOCUMENT_TYPES.other;

  // Compact mode - just a button
  if (compact) {
    return (
      <>
        <Button
          variant="outlined"
          startIcon={<CloudUpload />}
          onClick={handleFileClick}
          size="small"
        >
          Upload Document
        </Button>
        {queuedName && (
          <Typography variant="caption" sx={{ ml: 1 }}>
            {queuedName} attached <Button size="small" onClick={removeQueued}>Remove</Button>
          </Typography>
        )}
        
        {/* Hidden file inputs */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*,.pdf"
          onChange={handleFileSelect}
        />
        <input
          type="file"
          ref={cameraInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
        />
        
        {/* Upload Dialog */}
        <UploadDialog
          open={dialogOpen}
          onClose={handleCancel}
          selectedFile={selectedFile}
          preview={preview}
          documentType={documentType}
          setDocumentType={setDocumentType}
          fitNoteStart={fitNoteStart}
          setFitNoteStart={setFitNoteStart}
          fitNoteEnd={fitNoteEnd}
          setFitNoteEnd={setFitNoteEnd}
          fitNoteStatus={fitNoteStatus}
          setFitNoteStatus={setFitNoteStatus}
          notes={notes}
          setNotes={setNotes}
          uploading={uploading}
          uploadProgress={uploadProgress}
          error={error}
          onUpload={handleUpload}
          onCancel={handleCancel}
          queueMode={queueMode}
        />
      </>
    );
  }

  // Full upload card
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Upload Document
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Upload buttons */}
      <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
        {/* Camera button - mobile friendly */}
        <Button
          variant="contained"
          color="primary"
          startIcon={<CameraAlt />}
          onClick={handleCameraCapture}
          sx={{ flex: 1, minWidth: 150 }}
        >
          Take Photo
        </Button>
        
        {/* File selection button */}
        <Button
          variant="outlined"
          startIcon={<InsertDriveFile />}
          onClick={handleFileClick}
          sx={{ flex: 1, minWidth: 150 }}
        >
          Choose File
        </Button>
      </Box>
      
      <Typography variant="caption" color="text.secondary">
        Supported: Photos (JPG, PNG), PDF • Max size: 10MB
      </Typography>

      {queuedName && (
        <Alert severity="success" sx={{ mt: 2 }} action={<Button color="inherit" size="small" onClick={removeQueued}>Remove</Button>}>
          {queuedName} is attached. It will be sent when you submit your request.
        </Alert>
      )}
      
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*,.pdf"
        onChange={handleFileSelect}
      />
      <input
        type="file"
        ref={cameraInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
      />
      
      {/* Upload Dialog */}
      <UploadDialog
        open={dialogOpen}
        onClose={handleCancel}
        selectedFile={selectedFile}
        preview={preview}
        documentType={documentType}
        setDocumentType={setDocumentType}
        fitNoteStart={fitNoteStart}
        setFitNoteStart={setFitNoteStart}
        fitNoteEnd={fitNoteEnd}
        setFitNoteEnd={setFitNoteEnd}
        fitNoteStatus={fitNoteStatus}
        setFitNoteStatus={setFitNoteStatus}
        notes={notes}
        setNotes={setNotes}
        uploading={uploading}
        uploadProgress={uploadProgress}
        error={error}
        onUpload={handleUpload}
        onCancel={handleCancel}
        queueMode={queueMode}
      />
    </Paper>
  );
};


/**
 * Upload Dialog Component
 * Shows preview and collects additional information
 */
const UploadDialog = ({
  open,
  onClose,
  selectedFile,
  preview,
  documentType,
  setDocumentType,
  fitNoteStart,
  setFitNoteStart,
  fitNoteEnd,
  setFitNoteEnd,
  fitNoteStatus,
  setFitNoteStatus,
  notes,
  setNotes,
  uploading,
  uploadProgress,
  error,
  onUpload,
  onCancel,
  queueMode
}) => {
  const typeConfig = DOCUMENT_TYPES[documentType] || DOCUMENT_TYPES.other;
  
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        Upload Document
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Preview */}
        {selectedFile && (
          <Box sx={{ mb: 2 }}>
            {preview ? (
              <Box
                component="img"
                src={preview}
                alt="Preview"
                sx={{
                  width: '100%',
                  maxHeight: 300,
                  objectFit: 'contain',
                  borderRadius: 1,
                  backgroundColor: 'grey.100'
                }}
              />
            ) : (
              <Box 
                sx={{ 
                  p: 3, 
                  textAlign: 'center', 
                  backgroundColor: 'grey.100',
                  borderRadius: 1 
                }}
              >
                <PictureAsPdf sx={{ fontSize: 48, color: 'error.main' }} />
                <Typography>{selectedFile.name}</Typography>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
            </Typography>
          </Box>
        )}
        
        {/* Document type selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Document Type</InputLabel>
          <Select
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            label="Document Type"
          >
            {Object.entries(DOCUMENT_TYPES).map(([key, config]) => (
              <MenuItem key={key} value={key}>
                {config.icon} {config.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{typeConfig.description}</FormHelperText>
        </FormControl>
        
        {/* Fit note specific fields */}
        {documentType === 'fit_note' && (
          <>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Fit Note Details
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Period Start"
                  value={fitNoteStart}
                  onChange={(e) => setFitNoteStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Period End"
                  value={fitNoteEnd}
                  onChange={(e) => setFitNoteEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
            </Grid>
            
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Fit Note Status</InputLabel>
              <Select
                value={fitNoteStatus}
                onChange={(e) => setFitNoteStatus(e.target.value)}
                label="Fit Note Status"
                size="small"
              >
                <MenuItem value="not_fit">
                  ❌ Not fit for work
                </MenuItem>
                <MenuItem value="may_be_fit">
                  ⚠️ May be fit for work (with adjustments)
                </MenuItem>
              </Select>
              <FormHelperText>
                Select the option indicated on the fit note
              </FormHelperText>
            </FormControl>
          </>
        )}
        
        {/* Optional notes */}
        <TextField
          fullWidth
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={2}
          sx={{ mt: 2 }}
          placeholder="Any additional information..."
        />
        
        {/* Upload progress */}
        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Uploading... {uploadProgress}%
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onCancel} disabled={uploading}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={onUpload}
          disabled={!selectedFile || uploading}
          startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
        >
          {uploading ? 'Uploading...' : (queueMode ? 'Attach' : 'Upload')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


/**
 * Document List Component
 * Shows all documents attached to a record with view/download/verify options.
 * 
 * Props:
 *   sourceTable: 'absences' | 'time_off_requests' | 'flexible_working'
 *   sourceId: ID of the parent record
 *   canVerify: Boolean - show verify button for managers
 *   onDocumentChange: Callback when documents change
 */
export const DocumentList = ({ 
  sourceTable, 
  sourceId, 
  canVerify = false,
  onDocumentChange 
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/time-off-docs/record/${sourceTable}/${sourceId}`);
      setDocuments(response.data.documents || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [sourceTable, sourceId]);

  React.useEffect(() => {
    if (sourceId) {
      fetchDocuments();
    }
  }, [sourceId, fetchDocuments]);

  // View document
  const handleView = async (doc) => {
    setViewingDoc(doc);
    setViewLoading(true);
    setViewData(null);
    
    try {
      const response = await api.get(`/time-off-docs/${doc.id}/view`);
      setViewData(response.data);
    } catch (err) {
      console.error('Error viewing document:', err);
      setError('Failed to load document');
    } finally {
      setViewLoading(false);
    }
  };

  // Download document
  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/time-off-docs/${doc.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading document:', err);
      setError('Failed to download document');
    }
  };

  // Verify document (managers only)
  const handleVerify = async (doc) => {
    try {
      await api.post(`/time-off-docs/${doc.id}/verify`, {});
      fetchDocuments();
      if (onDocumentChange) onDocumentChange();
    } catch (err) {
      console.error('Error verifying document:', err);
      setError('Failed to verify document');
    }
  };

  // Delete document
  const handleDelete = async (doc) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await api.delete(`/time-off-docs/${doc.id}`);
      fetchDocuments();
      if (onDocumentChange) onDocumentChange();
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(err.response?.data?.error || 'Failed to delete document');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (documents.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
        No documents uploaded
      </Typography>
    );
  }

  return (
    <>
      <List dense>
        {documents.map((doc) => (
          <ListItem
            key={doc.id}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mb: 1,
              backgroundColor: doc.is_verified ? 'success.lighter' : 'background.paper'
            }}
          >
            <ListItemIcon>
              {doc.is_image ? (
                <ImageIcon color="primary" />
              ) : doc.is_pdf ? (
                <PictureAsPdf color="error" />
              ) : (
                <InsertDriveFile />
              )}
            </ListItemIcon>
            
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight="medium">
                    {doc.document_type_name}
                  </Typography>
                  {doc.is_verified && (
                    <Chip
                      icon={<VerifiedUser />}
                      label="Verified"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Box>
              }
              secondary={
                <>
                  <Typography variant="caption" display="block">
                    {doc.original_filename} • {doc.file_size_display}
                  </Typography>
                  {doc.fit_note_start && doc.fit_note_end && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Period: {doc.fit_note_start} to {doc.fit_note_end}
                      {doc.fit_note_status_display && ` • ${doc.fit_note_status_display}`}
                    </Typography>
                  )}
                </>
              }
            />
            
            <ListItemSecondaryAction>
              <Tooltip title="View">
                <IconButton size="small" onClick={() => handleView(doc)}>
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton size="small" onClick={() => handleDownload(doc)}>
                  <Download fontSize="small" />
                </IconButton>
              </Tooltip>
              {canVerify && !doc.is_verified && (
                <Tooltip title="Mark as Verified">
                  <IconButton size="small" color="success" onClick={() => handleVerify(doc)}>
                    <CheckCircle fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!doc.is_verified && (
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => handleDelete(doc)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>

      {/* Document Viewer Dialog */}
      <Dialog 
        open={!!viewingDoc} 
        onClose={() => setViewingDoc(null)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {viewingDoc?.document_type_name}
          {viewingDoc?.is_verified && (
            <Chip
              icon={<VerifiedUser />}
              label="Verified"
              size="small"
              color="success"
              sx={{ ml: 1 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {viewLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : viewData ? (
            <>
              {!viewData.integrity_valid && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Warning sx={{ mr: 1 }} />
                  Document integrity check failed. File may have been modified.
                </Alert>
              )}
              
              {viewData.mime_type?.startsWith('image/') ? (
                <Box
                  component="img"
                  src={`data:${viewData.mime_type};base64,${viewData.data}`}
                  alt={viewData.filename}
                  sx={{
                    width: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain'
                  }}
                />
              ) : viewData.mime_type === 'application/pdf' ? (
                <Box sx={{ height: '70vh' }}>
                  <iframe
                    src={`data:application/pdf;base64,${viewData.data}`}
                    title={viewData.filename}
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                  />
                </Box>
              ) : (
                <Alert severity="info">
                  This document type cannot be previewed. Please download to view.
                </Alert>
              )}
            </>
          ) : (
            <Alert severity="error">Failed to load document</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingDoc(null)}>Close</Button>
          {viewingDoc && (
            <Button 
              variant="contained" 
              startIcon={<Download />}
              onClick={() => handleDownload(viewingDoc)}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};


/**
 * Fit Note Upload Card
 * Simplified component specifically for fit note uploads.
 * Perfect for embedding in absence forms.
 */
export const FitNoteUploadCard = ({ absenceId, onUploadComplete }) => {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          📋 Fit Note
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          If your absence is longer than 7 days, you need to provide a fit note from your GP.
        </Typography>
        
        <DocumentUpload
          sourceTable="absences"
          sourceId={absenceId}
          documentType="fit_note"
          onUploadComplete={onUploadComplete}
        />
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle2" gutterBottom>
          Uploaded Documents
        </Typography>
        <DocumentList
          sourceTable="absences"
          sourceId={absenceId}
          canVerify={false}
        />
      </CardContent>
    </Card>
  );
};


export default DocumentUpload;